import { useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFiles } from "@/hooks/queries";
import { useDebounce } from "@/hooks/useDebounce";
import { FILE_KINDS, formatBytes } from "@/lib/files";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

export function FilePickerDialog({ open, onOpenChange, onPick, excludeIds = [] }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const query = useDebounce(q.trim(), 250);
  const { data, isLoading, isFetching } = useFiles({ q: query, ready: true, limit: 50 });
  const items = (data?.items || []).filter((f) => !excludeIds.includes(f.id));
  const toggle = (f) => setPicked((list) => (list.some((x) => x.id === f.id) ? list.filter((x) => x.id !== f.id) : [...list, f]));
  const done = () => {
    onPick(picked);
    setPicked([]);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-lg" data-testid="file-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Attach from your files</DialogTitle>
        </DialogHeader>
        <div className="mt-2 flex h-11 items-center gap-2.5 rounded-xl border border-white/10 bg-ink-900 px-3 focus-within:border-white/25">
          <Search className="h-4 w-4 text-zinc-500" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" className="h-full flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none" data-testid="file-picker-search" />
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
        </div>
        <div className="mt-3 max-h-[340px] space-y-0.5 overflow-y-auto" data-testid="file-picker-list">
          {!isLoading && items.length === 0 && <p className="py-10 text-center text-sm text-zinc-500" data-testid="file-picker-empty">{query ? "No ready files match." : "No ready files yet. Upload one from your device."}</p>}
          {items.map((f) => {
            const Icon = FILE_KINDS[f.kind]?.icon || FILE_KINDS.txt.icon;
            const on = picked.some((x) => x.id === f.id);
            return (
              <button key={f.id} type="button" onClick={() => toggle(f)} data-testid={`file-picker-item-${f.id}`} className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors", on ? "bg-white/[0.07]" : "hover:bg-white/[0.04]")}>
                <Icon className="h-4 w-4 shrink-0 text-zinc-400" strokeWidth={1.6} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-zinc-100">{f.name}</span>
                  <span className="block text-[11.5px] text-zinc-500">{FILE_KINDS[f.kind]?.label} · {formatBytes(f.size_bytes)} · {formatRelative(f.created_at)}</span>
                </span>
                <span className={cn("flex h-5 w-5 items-center justify-center rounded-md border", on ? "border-white bg-white text-ink-950" : "border-white/20")}>{on && <Check className="h-3.5 w-3.5" strokeWidth={2.4} />}</span>
              </button>
            );
          })}
        </div>
        <DialogFooter className="mt-3 gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:bg-white/[0.05]" data-testid="file-picker-cancel">Cancel</button>
          <button type="button" onClick={done} disabled={!picked.length} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950 disabled:opacity-40" data-testid="file-picker-attach">
            Attach{picked.length ? ` ${picked.length}` : ""}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
